import {
  AddFileCommentAndMoveToExceptionPayload,
  AddQcTableRowPayload,
  DeleteQaTableRowMultiplePayload,
  FileHeaderData,
  GetFileCommentsPayload,
  GetFileCommentsResponse,
  GetFileExceptionReasonList,
  GetFileHeaderDataPayload,
  GetFileHeaderDataResponse,
  GetQaTablesFieldsListPayload,
  GetQaTablesFieldsListResponse,
  GetQaTablesListPayload,
  ImageViewerGetDocumentBytesPayload,
  ImageViewerGetDocumentBytesResponse,
  ImageViewerGetFileLevelPayload,
  ImageViewerGetFileLevelResponse,
  ImageViewerGetFilePDFPayload,
  MergeQcTableRowsPayload,
  QaTablesListTypes,
  QcTableRowData,
  SearchTextInFilePayload,
  SearchTextInFileResponse,
  SplitQcTablePayload,
  UpdateFieldHeaderDataPayload,
  UpdateFileStatusPayload,
  UpdateQcTableCellPayload,
  UpdateQcTableRowPayload
} from 'types/image-viewer';
import baseApi, { API_V1 } from './base';
import { convertFieldChildDataToHeaderData, convertTableFieldsListToUI } from 'utils/imageViewerUtils';
import { convertToTiff } from 'workers/utils';

const imageViewerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getImageViewerDocumentByte: build.query<ImageViewerGetDocumentBytesResponse, ImageViewerGetDocumentBytesPayload>({
      query: ({ batchID, fileID, pageNo, imageFormat = 'tiff' }) => ({
        url: `${API_V1}/Assurance/GetDocumentBytes`,
        params: {
          BatchID: batchID,
          FileID: fileID,
          PageNo: pageNo,
          ImageFormat: imageFormat
        }
      }),
      transformResponse: async (responseData: any) => {
        const base64Png = await convertToTiff({ base64Tiff: responseData.data });
        return { pageBytes: base64Png || null } as any;
      }
    }),
    getImageViewerFileLevelDetails: build.query<ImageViewerGetFileLevelResponse, ImageViewerGetFileLevelPayload>({
      query: ({ fileID }) => ({
        url: `${API_V1}/Assurance/GetFileLevelDetails`,
        params: {
          fileID
        }
      }),
      transformResponse: (responseData: { data: ImageViewerGetFileLevelResponse }) => responseData.data
    }),
    getImageViewerPdfFile: build.query<string | null, ImageViewerGetFilePDFPayload>({
      query: ({ fileId }) => ({
        url: `${API_V1}/Assurance/GetPDF`,
        params: {
          fileId
        }
      }),
      transformResponse: (responseData: { data: string }) => responseData?.data || null
    }),
    getSearchTextInFile: build.query<SearchTextInFileResponse, SearchTextInFilePayload>({
      query: ({ fileID, searchText }) => ({
        url: `${API_V1}/Assurance/SearchTextInFile`,
        params: {
          fileID,
          searchText
        }
      }),
      transformResponse: (responseData: { data: SearchTextInFileResponse }) => responseData.data
    }),
    getFileExceptionList: build.query<GetFileExceptionReasonList[], void>({
      query: () => ({
        url: `${API_V1}/FileException/GetFileException`
      })
    }),
    addFileComment: build.mutation<void, AddFileCommentAndMoveToExceptionPayload>({
      query: (params) => ({
        url: `${API_V1}/FileException/InsertComment`,
        method: 'put',
        params
      })
    }),
    getFileComments: build.query<GetFileCommentsResponse[], GetFileCommentsPayload>({
      query: ({ fileID }) => ({
        url: `${API_V1}/FileException/GetComments`,
        params: {
          fileID
        }
      }),
      transformResponse: (response: GetFileCommentsResponse[] | string) => {
        if (typeof response === 'object') {
          return response;
        } else {
          return [];
        }
      }
    }),
    getFileHeaderData: build.query<GetFileHeaderDataResponse, GetFileHeaderDataPayload>({
      query: ({ fileId, batchID }) => ({
        url: `${API_V1}/Assurance/GetQAHeaderData`,
        params: {
          fileId,
          batchID
        }
      }),
      transformResponse: (responseData: { data: GetFileHeaderDataResponse }) => {
        const returnData: GetFileHeaderDataResponse = { headerData: [], filedetail: {} as GetFileHeaderDataResponse['filedetail'] };
        if (responseData.data) {
          const headerData = responseData.data.headerData || [];
          returnData.headerData = headerData.map(({ childData, ...headerData }) =>
            convertFieldChildDataToHeaderData(headerData as FileHeaderData, childData as any)
          );
          returnData.filedetail = responseData.data.filedetail;
        }
        return returnData;
      },
      providesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    // eslint-disable-next-line prettier/prettier
    updateFileHeaderData: build.mutation<GetFileHeaderDataResponse, UpdateFieldHeaderDataPayload>({
      query: (data) => ({
        url: `${API_V1}/Assurance/UpdateHeaderData`,
        method: 'POST',
        cheaders: 'application/*+json',
        data
      }),
      transformResponse: (responseData: { data: GetFileHeaderDataResponse }) => {
        const returnData: GetFileHeaderDataResponse = { headerData: [], filedetail: {} as GetFileHeaderDataResponse['filedetail'] };
        if (responseData.data) {
          const headerData = responseData.data.headerData || [];
          returnData.headerData = headerData.map(({ childData, ...headerData }) =>
            convertFieldChildDataToHeaderData(headerData as FileHeaderData, childData as any)
          );
          returnData.filedetail = responseData.data.filedetail;
        }
        return returnData;
      }
    }),
    updateFileStatus: build.mutation<any, UpdateFileStatusPayload>({
      query: (params) => ({
        url: `${API_V1}/Assurance/FileStatus`,
        method: 'PUT',
        params
      })
    }),
    getQaFileTables: build.query<QaTablesListTypes[], GetQaTablesListPayload>({
      query: ({ fileId, fieldId = 0 }) => ({
        url: `${API_V1}/QATable/tables`,
        params: {
          fileId,
          fieldId
        }
      })
    }),
    getQaTableFields: build.query<QcTableRowData, GetQaTablesFieldsListPayload>({
      query: ({ fileId, fieldId = 0 }) => ({
        url: `${API_V1}/QATable`,
        params: {
          fileId,
          fieldId
        }
      }),
      transformResponse: (data: GetQaTablesFieldsListResponse[]) => convertTableFieldsListToUI(data)
    }),
    updateQaTableCell: build.mutation<void, UpdateQcTableCellPayload>({
      query: (params) => ({
        url: `${API_V1}/QATable`,
        method: 'PUT',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    clearQaTableCell: build.mutation<void, UpdateQcTableRowPayload>({
      query: (params) => ({
        url: `${API_V1}/QATable/Clear`,
        method: 'PUT',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    revertQaTableCell: build.mutation<void, UpdateQcTableRowPayload>({
      query: (params) => ({
        url: `${API_V1}/QATable/Revert`,
        method: 'PUT',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    deleteQaTableRow: build.mutation<void, UpdateQcTableRowPayload>({
      query: (params) => ({
        url: `${API_V1}/QATable`,
        method: 'DELETE',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    addNewQaTableRow: build.mutation<void, AddQcTableRowPayload>({
      query: (params) => ({
        url: `${API_V1}/QATable`,
        method: 'POST',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    mergeQaTableRows: build.mutation<void, MergeQcTableRowsPayload>({
      query: (params) => ({
        url: `${API_V1}/QATable/Merge`,
        method: 'PUT',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    splitQaTable: build.mutation<void, SplitQcTablePayload>({
      query: (data) => ({
        url: `${API_V1}/QATable/Split`,
        method: 'PUT',
        cheaders: 'application/*+json',
        data
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    }),
    deleteQaTableMultipleRows: build.mutation<void, DeleteQaTableRowMultiplePayload>({
      query: (params) => ({
        url: `${API_V1}/QATable/multiple`,
        method: 'DELETE',
        params
      }),
      invalidatesTags: ['assuranceImageViewerHeaderData' as any]
    })
  })
});

export const {
  useGetImageViewerDocumentByteQuery,
  useLazyGetImageViewerDocumentByteQuery,
  useGetImageViewerFileLevelDetailsQuery,
  useLazyGetImageViewerPdfFileQuery,
  useLazyGetSearchTextInFileQuery,
  useGetFileExceptionListQuery,
  useAddFileCommentMutation,
  useGetFileCommentsQuery,
  useLazyGetFileCommentsQuery,
  useGetFileHeaderDataQuery,
  useUpdateFileHeaderDataMutation,
  useUpdateFileStatusMutation,
  useGetQaFileTablesQuery,
  useGetQaTableFieldsQuery,
  useUpdateQaTableCellMutation,
  useClearQaTableCellMutation,
  useRevertQaTableCellMutation,
  useDeleteQaTableRowMutation,
  useAddNewQaTableRowMutation,
  useMergeQaTableRowsMutation,
  useSplitQaTableMutation,
  useLazyGetQaTableFieldsQuery,
  useDeleteQaTableMultipleRowsMutation
} = imageViewerApi;

export default imageViewerApi;
